import { Box } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { GhostButton } from '../Dashboard/components/GhostButton';
import type { SchoolRow } from './schoolRows';
import { SCHOOLS } from './tokens';

type Props = {
  rows: SchoolRow[];
  visibleRows?: SchoolRow[];
  onView: (name: string) => void;
};

const COLUMN_SX = {
  school: { width: SCHOOLS.columns.school },
  students: { width: SCHOOLS.columns.students },
  newcomers: { width: SCHOOLS.columns.newcomers },
  ltels: { width: SCHOOLS.columns.ltels },
  view: { flexGrow: 1, flexBasis: 0 },
} as const;

const CELL_SX = {
  display: 'flex',
  alignItems: 'center',
  p: '8px 16px',
} as const;

function cellText(weight: 500 | 700) {
  return {
    fontFamily: SCHOOLS.font,
    fontSize: 16,
    fontWeight: weight,
    lineHeight: '24px',
    color: weight === 700 ? SCHOOLS.ink : SCHOOLS.muted,
  } as const;
}

const NUMERIC_COLUMNS = [
  { key: 'students', field: 'students' },
  { key: 'newcomers', field: 'newcomers' },
  { key: 'ltels', field: 'ltels' },
] as const;

const ROW_BORDER = `1px solid ${SCHOOLS.border}`;

function HeaderCell({ column, label }: { column: keyof typeof COLUMN_SX; label: string }) {
  return (
    <Box
      role="columnheader"
      data-testid={`school-header-${column}`}
      sx={{ ...CELL_SX, ...COLUMN_SX[column] }}
    >
      <Box component="span" sx={cellText(700)}>
        {label}
      </Box>
    </Box>
  );
}

export function SchoolsTable({ rows, visibleRows = rows, onView }: Props) {
  const visibleNames = new Set(visibleRows.map((row) => row.name));
  const tableRows = rows
    .map((row) => ({
      school: row.name,
      students: row.students,
      newcomers: row.newcomers,
      ltels: row.ltels,
    }))
    .filter((row) => visibleNames.has(row.school));
  return (
    <Box
      role="table"
      aria-label="Schools"
      data-testid="schools-table"
      sx={{ display: 'flex', flexDirection: 'column' }}
    >
      <Box role="row" sx={{ display: 'flex', borderBottom: ROW_BORDER }}>
        <HeaderCell column="school" label="School" />
        <HeaderCell column="students" label="Students" />
        <HeaderCell column="newcomers" label="Newcomers" />
        <HeaderCell column="ltels" label="LTELs" />
        <Box role="columnheader" sx={{ ...CELL_SX, ...COLUMN_SX.view }} />
      </Box>
      {tableRows.map((row, index) => (
        <Box
          key={row.school}
          role="row"
          data-testid="school-row"
          sx={{
            display: 'flex',
            ...(index < tableRows.length - 1 ? { borderBottom: ROW_BORDER } : {}),
          }}
        >
          <Box role="cell" sx={{ ...CELL_SX, ...COLUMN_SX.school }}>
            <Box component="span" data-testid="school-name" sx={cellText(700)}>
              {row.school}
            </Box>
          </Box>
          {NUMERIC_COLUMNS.map(({ key, field }) => (
            <Box role="cell" key={key} sx={{ ...CELL_SX, ...COLUMN_SX[key] }}>
              <Box component="span" data-testid="school-count" sx={cellText(500)}>
                {row[field]}
              </Box>
            </Box>
          ))}
          <Box
            role="cell"
            sx={{ ...CELL_SX, ...COLUMN_SX.view, justifyContent: 'flex-end', p: '4px 16px' }}
          >
            <GhostButton
              label="View"
              ariaLabel={`View ${row.school}`}
              onClick={() => onView(row.school)}
              endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
}
